import {
  Action,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource
} from 'modules/types/common';
import { AnyAction } from 'redux';
import { DFDHeaders } from 'serverTypes/authentication';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { Thunk, RootDispatch } from './types';

/**
 * Pipes a list of async actions through dispatch, returning the first error, or success
 * @param dispatch Redux Dispatch
 * @param actions List of actions/actionCreators to execute in order
 * @param errorType ActionType to dispatch on error
 * @param successType ActionType to dispatch on success
 */
export async function pipeWithError(
  dispatch: RootDispatch,
  actions: Array<Thunk | AnyAction>,
  startType: string,
  errorType: string,
  successType: string
) {
  dispatch({ type: startType });

  for (const action of actions) {
    /* eslint-disable-next-line no-await-in-loop */
    const res = await dispatch(action);

    if (res && res.error) {
      return dispatch({ type: errorType, error: res.error });
    }
  }

  return dispatch({ type: successType });
}

export const isActionOfType = (actionType: string | string[], action?: AnyAction) => {
  if (!action || !action.type) {
    return false;
  }

  const typeList = Array.isArray(actionType) ? actionType : [actionType];

  return typeList.includes(action.type);
};

export const getActionMetadata = (action?: AnyAction) => {
  return action && action.meta ? action.meta : {};
};

// A counterpart to redux-start-kit's createAction that will maintain typeSafety but allows an error parameter
export function createErrorAction<E = Error, T extends string = string>(type: T) {
  function actionCreator(error: E) {
    return { type, error };
  }

  actionCreator.toString = (): T => `${type}` as T;
  actionCreator.type = type as T;

  return actionCreator;
}

export function getDocumentDecorator(type: string) {
  return (documentId: string, acceptType = 'application/json+fhir'): Action => {
    const request = {
      headers: {
        [DFDHeaders.contentId]: documentId,
        [DFDHeaders.mhpAdapter]: 'utah',
        accept: acceptType
      },
      url: getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.BINARY,
        DispatcherEndpoint.ID
      )
    };
    return {
      type,
      payload: {
        client: 'dispatcher',
        request
      }
    };
  };
}
